import { MatterExpensesTable } from "@/components/features/matter/expenses/MatterExpensesTable";
import { MatterLiensTable } from "@/components/features/matter/liens/MatterLiensTable";
import { MatterTreatmentBillsDataTable } from "@/components/features/matter/medical-treatments/MatterTreatmentBillsCard/MatterTreatmentBillsDataTable";
import { Button, Text } from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentBillViewModel } from "@/hooks/matter-view-models/use-medical-treatment-bill-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Matter } from "@/lib/types";
import { ExpenseIcon, LienIcon } from "@/lib/utils/iconUtils";
import { useState } from "react";

interface DamagesCardProps {
  matter: Matter;
}

export const DamagesCard = ({ matter }: DamagesCardProps) => {
  const { hasActionPermission } = useUser();
  const { openExpenseModal, openLienModal } = useGlobalModal();
  const [isMedicalBillsExpanded, setIsMedicalBillsExpanded] = useState("none");
  const medicalTreatmentBills = useMedicalTreatmentBillViewModel(matter.id);

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-2">
      {/* Medical Bills Accordion */}
      <Accordion
        type="single"
        collapsible
        value={"medicalBills"}
        onValueChange={() => {
          setIsMedicalBillsExpanded(
            isMedicalBillsExpanded === "medicalBills" ? "none" : "medicalBills",
          );
        }}
        className="bg-card w-full rounded-lg border-none shadow-sm"
      >
        <AccordionItem value="medicalBills" className="border-none">
          <div className="flex grow items-center gap-x-2 p-4">
            <Text
              variant="body"
              color="primary"
              className="text-lg font-semibold"
            >
              Medical bills
            </Text>
          </div>

          <AccordionContent className="px-4 py-6">
            <div className="grid grid-cols-1 gap-4">
              <MatterTreatmentBillsDataTable
                bills={medicalTreatmentBills}
                matter={matter}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion
        type="single"
        collapsible
        value={"liens"}
        className="bg-card w-full rounded-lg border-none shadow-sm"
      >
        <AccordionItem value="liens" className="border-none">
          <div className="flex grow items-center gap-x-2 p-4">
            <Text
              variant="body"
              color="primary"
              className="text-lg font-semibold"
            >
              Liens
            </Text>
          </div>
          <AccordionContent className="px-4 py-6">
            <div className="grid grid-cols-1 gap-4">
              <MatterLiensTable
                matter={matter}
                tableButtons={
                  <>
                    {hasActionPermission(
                      ACTION_PERMISSION_KEYS.matterLien,
                      ACTION_PERMISSION_VALUES.create,
                    ) && (
                      <Button
                        size="sm"
                        className="bg-light-primary/50 hover:bg-primary/25 text-primary outline-primary/50 outline"
                        onClick={() => {
                          openLienModal({
                            matterId: matter.id,
                            lienId: undefined,
                            editing: false,
                          });
                        }}
                      >
                        <LienIcon className="mr-2 h-4 w-4" />
                        Add lien
                      </Button>
                    )}
                  </>
                }
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion
        type="single"
        collapsible
        value={"expenses"}
        defaultValue="expenses"
        className="bg-card w-full rounded-lg border-none shadow-sm"
      >
        <AccordionItem value="expenses" className="border-none">
          <div className="flex grow items-center gap-x-2 p-4">
            <Text
              variant="body"
              color="primary"
              className="text-lg font-semibold"
            >
              Costs
            </Text>
          </div>
          <AccordionContent className="px-4 py-6">
            <div className="grid grid-cols-1 gap-4">
              <MatterExpensesTable
                matter={matter}
                tableButtons={
                  <>
                    {hasActionPermission(
                      ACTION_PERMISSION_KEYS.matterExpense,
                      ACTION_PERMISSION_VALUES.create,
                    ) && (
                      <Button
                        size="sm"
                        className="bg-light-primary/50 hover:bg-primary/25 text-primary outline-primary/50 outline"
                        onClick={() => {
                          openExpenseModal({
                            matterId: matter.id,
                            editing: false,
                            initialTab: "details",
                          });
                        }}
                      >
                        <ExpenseIcon className="mr-2 h-4 w-4" />
                        Add cost
                      </Button>
                    )}
                  </>
                }
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
    //   </CardContent>
    // </Card>
  );
};
